import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { employeeService } from '../../services/employeeService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import Swal from 'sweetalert2';
import api from '../../config/api';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const Profile = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [profile, setProfile] = useState(null);
    const [jobHistory, setJobHistory] = useState([]);
    const [educationHistory, setEducationHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [jobTitles, setJobTitles] = useState([]);
    const [imageTimestamp, setImageTimestamp] = useState(Date.now());

    useEffect(() => {
        loadProfileData();
        loadJobTitles();
    }, []);

    const refreshUserData = async () => {
        try {
            const response = await authService.getProfile();
            if (response.success && response.data?.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
                // Trigger a custom event to notify components of user data update
                window.dispatchEvent(new Event('userDataUpdated'));
            }
        } catch (error) {
            console.error('Error refreshing user data:', error);
        }
    };

    const loadJobTitles = async () => {
        try {
            const response = await api.get('/job-titles');
            if (Array.isArray(response.data)) {
                setJobTitles(response.data);
            } else if (response.data?.data) {
                setJobTitles(response.data.data);
            }
        } catch (error) {
            console.error('Error loading job titles:', error);
        }
    };

    const loadProfileData = async () => {
        try {
            setLoading(true);
            const [profileResponse, jobResponse, educationResponse] = await Promise.all([
                employeeService.getProfile(),
                employeeService.getJobHistory(),
                employeeService.getEducationHistory()
            ]);

            // Handle profile response - API returns EmployeeResource directly
            // Check if response is wrapped in data property
            if (profileResponse) {
                // If response has data property, use it; otherwise use response directly
                const profileData = profileResponse.data || profileResponse;
                setProfile(profileData);
            }

            // Handle job history - API returns collection directly
            if (Array.isArray(jobResponse)) {
                setJobHistory(jobResponse);
            } else if (jobResponse?.data) {
                setJobHistory(jobResponse.data);
            } else if (jobResponse?.success && jobResponse.data) {
                setJobHistory(jobResponse.data);
            }

            // Handle education history - API returns collection directly
            if (Array.isArray(educationResponse)) {
                setEducationHistory(educationResponse);
            } else if (educationResponse?.data) {
                setEducationHistory(educationResponse.data);
            } else if (educationResponse?.success && educationResponse.data) {
                setEducationHistory(educationResponse.data);
            }
        } catch (error) {
            console.error('Error loading profile data:', error);
            toast.error(t('employee.profile.loadingFailed'));
        } finally {
            setLoading(false);
        }
    };


    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date)) return '';

        const months = t('employee.profile.months');

        if (!Array.isArray(months)) {
            console.error("Months translation is not an array");
            return dateString;
        }

        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };


    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Kb';
        return (bytes / (1024 * 1024)).toFixed(0) + ' Mb';
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
        return `منذ ${diffYears} سنوات`;
    };

    const handleProfileImageClick = async () => {
        // Create a hidden file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png,image/jpeg,image/jpg,image/webp';
        input.style.display = 'none';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Validate file type
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error(t('employee.profile.imageFormatError'));
                return;
            }

            // Validate file size (5MB = 5 * 1024 * 1024 bytes)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                toast.error(t('employee.profile.imageSizeError'));
                return;
            }

            // Show loading
            const loadingToast = toast.loading(t('employee.profile.uploadingImage'));

            try {
                const formData = new FormData();
                formData.append('image', file);

                const updatedProfile = await employeeService.updateProfile(formData);
                // Refresh user data to update job_title_name in localStorage
                await refreshUserData();
                toast.dismiss(loadingToast);
                toast.success(t('employee.profile.imageUploadSuccess'));

                // Update image timestamp to force cache refresh
                setImageTimestamp(Date.now());

                // Update profile state immediately with the response
                if (updatedProfile) {
                    const profileData = updatedProfile.data || updatedProfile;
                    setProfile(profileData);
                }

                // Also reload to ensure all data is fresh
                await loadProfileData();
            } catch (error) {
                toast.dismiss(loadingToast);
                const message = error.response?.data?.message || t('employee.profile.imageUploadFailed');
                toast.error(message);
                console.error('Error updating profile image:', error);
            }
        };

        // Trigger file input
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    };

    const handleEditJobTitle = async () => {
        const currentJobTitleId = profile?.job_title_id || profile?.job_title?.id || '';
        const jobTitleOptions = jobTitles.map(jt =>
            `<option value="${jt.id}" ${jt.id === currentJobTitleId ? 'selected' : ''}>${jt.name}</option>`
        ).join('');
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';

        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">💼 ${t('employee.profile.editJobTitle')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            ${t('employee.profile.jobTitle')} <span style="color: #608fd3;">*</span>
                        </label>
                        <select id="job_title_id" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};" required>
                            <option value="">${t('employee.profile.selectJobTitle')}</option>
                            ${jobTitleOptions}
                        </select>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.save')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const jobTitleId = document.getElementById('job_title_id').value;

                if (!jobTitleId) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.jobTitleRequired')}`);
                    return false;
                }

                return {
                    job_title_id: parseInt(jobTitleId)
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.updateProfile(formValues);
                toast.success(t('employee.profile.jobTitleUpdateSuccess'));
                // Refresh user data to update job_title_name in localStorage
                await refreshUserData();
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.jobTitleUpdateFailed');
                toast.error(message);
            }
        }
    };

    const handleEditAbout = async () => {
        const currentBio = profile?.bio || '';
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';

        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">✏️ ${t('employee.profile.editAbout')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            ${t('employee.profile.personalBio')}
                        </label>
                        <textarea id="bio" class="swal2-textarea-modern" placeholder="${t('employee.profile.bioPlaceholder')}" rows="6" style="direction: ${dir}; text-align: ${textAlign};">${currentBio.replace(/"/g, '&quot;').replace(/'/g, '&#39;')}</textarea>
                        <small class="form-hint">${t('employee.profile.bioMaxLength')}</small>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.save')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const bio = document.getElementById('bio').value.trim();

                if (bio.length > 2000) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.bioMaxLength')}`);
                    return false;
                }

                return {
                    bio: bio || null
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.updateProfile(formValues);
                toast.success(t('employee.profile.bioUpdateSuccess'));
                // Refresh user data to update job_title_name in localStorage
                await refreshUserData();
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.bioUpdateFailed');
                toast.error(message);
            }
        }
    };

    const handleAddWorkExperience = async () => {
        const jobTitleOptions = jobTitles.map(jt => `<option value="${jt.id}">${jt.name}</option>`).join('');
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';

        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">✨ ${t('employee.profile.addWorkExperience')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            ${t('employee.profile.companyName')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="company_name" class="swal2-input-modern" placeholder="${t('employee.profile.companyNamePlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            ${t('employee.profile.jobTitle')} <span style="color: #608fd3;">*</span>
                        </label>
                        <select id="job_title_id" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};" required>
                            <option value="">${t('employee.profile.selectJobTitle')}</option>
                            ${jobTitleOptions}
                        </select>
                    </div>

                        <div class="form-field-group">
                            <label class="form-label">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                ${t('employee.profile.fromDate')} <span style="color: #608fd3;">*</span>
                            </label>
                            <input id="from_date" type="date" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};" required>
                        </div>

                        <div class="form-field-group">
                            <label class="form-label">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                ${t('employee.profile.toDate')}
                            </label>
                            <input id="to_date" type="date" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};">
                            <small class="form-hint">${t('employee.profile.toDateHint')}</small>
                        </div>


                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            ${t('employee.profile.description')}
                        </label>
                        <textarea id="description" class="swal2-textarea-modern" placeholder="${t('employee.profile.descriptionPlaceholder')}" rows="4" style="direction: ${dir}; text-align: ${textAlign};"></textarea>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.save')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const companyName = document.getElementById('company_name').value.trim();
                const jobTitleId = document.getElementById('job_title_id').value;
                const fromDate = document.getElementById('from_date').value;
                const toDate = document.getElementById('to_date').value;
                const description = document.getElementById('description').value.trim();

                if (!companyName || !jobTitleId || !fromDate) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.fillAllRequired')}`);
                    return false;
                }

                if (toDate && new Date(toDate) < new Date(fromDate)) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.endDateAfterStart')}`);
                    return false;
                }

                return {
                    company_name: companyName,
                    job_title_id: parseInt(jobTitleId),
                    from_date: fromDate,
                    to_date: toDate || null,
                    description: description || null
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.addJobHistory(formValues);
                toast.success(t('employee.profile.workExperienceAdded'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.workExperienceAddFailed');
                toast.error(message);
            }
        }
    };

    const handleEditWorkExperience = async (id) => {
        const currentJobHistory = Array.isArray(jobHistory) ? jobHistory : [];
        const job = currentJobHistory.find(j => j.id === id);
        if (!job) {
            toast.error(t('employee.profile.workExperienceNotFound'));
            return;
        }

        const currentJobTitleId = job.job_title_id || (job.job_title?.id);
        const jobTitleOptions = jobTitles.map(jt =>
            `<option value="${jt.id}" ${jt.id === currentJobTitleId ? 'selected' : ''}>${jt.name}</option>`
        ).join('');

        const fromDate = job.from_date ? job.from_date.split('T')[0] : '';
        const toDate = job.to_date ? job.to_date.split('T')[0] : '';
        const safeDescription = (job.description || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const safeCompanyName = (job.company_name || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';

        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">✏️ ${t('employee.profile.editWorkExperience')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            ${t('employee.profile.companyName')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="company_name" class="swal2-input-modern" placeholder="${t('employee.profile.companyNamePlaceholder')}" value="${safeCompanyName}" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            ${t('employee.profile.jobTitle')} <span style="color: #608fd3;">*</span>
                        </label>
                        <select id="job_title_id" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};" required>
                            <option value="">${t('employee.profile.selectJobTitle')}</option>
                            ${jobTitleOptions}
                        </select>
                    </div>

                        <div class="form-field-group">
                            <label class="form-label">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                ${t('employee.profile.fromDate')} <span style="color: #608fd3;">*</span>
                            </label>
                            <input id="from_date" type="date" class="swal2-input-modern" value="${fromDate}" style="direction: ${dir}; text-align: ${textAlign};" required>
                        </div>

                        <div class="form-field-group">
                            <label class="form-label">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                ${t('employee.profile.toDate')}
                            </label>
                            <input id="to_date" type="date" class="swal2-input-modern" value="${toDate}" style="direction: ${dir}; text-align: ${textAlign};">
                            <small class="form-hint">${t('employee.profile.toDateHint')}</small>
                        </div>


                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            ${t('employee.profile.description')}
                        </label>
                        <textarea id="description" class="swal2-textarea-modern" placeholder="${t('employee.profile.descriptionPlaceholder')}" rows="4" style="direction: ${dir}; text-align: ${textAlign};">${safeDescription}</textarea>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.saveChanges')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const companyName = document.getElementById('company_name').value.trim();
                const jobTitleId = document.getElementById('job_title_id').value;
                const fromDate = document.getElementById('from_date').value;
                const toDate = document.getElementById('to_date').value;
                const description = document.getElementById('description').value.trim();

                if (!companyName || !jobTitleId || !fromDate) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.fillAllRequired')}`);
                    return false;
                }

                if (toDate && new Date(toDate) < new Date(fromDate)) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.endDateAfterStart')}`);
                    return false;
                }

                return {
                    company_name: companyName,
                    job_title_id: parseInt(jobTitleId),
                    from_date: fromDate,
                    to_date: toDate || null,
                    description: description || null
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.updateJobHistory(id, formValues);
                toast.success(t('employee.profile.workExperienceUpdated'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.workExperienceUpdateFailed');
                toast.error(message);
            }
        }
    };

    const handleAddEducation = async () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';
        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">🎓 ${t('employee.profile.addEducation')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                            </svg>
                            ${t('employee.profile.academicDegree')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="academic_degree" class="swal2-input-modern" placeholder="${t('employee.profile.academicDegreePlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            ${t('employee.profile.educationAuthority')}
                        </label>
                        <input id="education_authority" class="swal2-input-modern" placeholder="${t('employee.profile.educationAuthorityPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            ${t('employee.profile.specialization')}
                        </label>
                        <input id="specialization" class="swal2-input-modern" placeholder="${t('employee.profile.specializationPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                            </svg>
                            ${t('employee.profile.grade')}
                        </label>
                        <input id="grade" class="swal2-input-modern" placeholder="${t('employee.profile.gradePlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            ${t('employee.profile.fromDate')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="from_date" type="date" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            ${t('employee.profile.toDate')}
                        </label>
                        <input id="to_date" type="date" class="swal2-input-modern" style="direction: ${dir}; text-align: ${textAlign};">
                        <small class="form-hint">${t('employee.profile.graduationDate')}</small>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.save')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const academicDegree = document.getElementById('academic_degree').value.trim();
                const educationAuthority = document.getElementById('education_authority').value.trim();
                const specialization = document.getElementById('specialization').value.trim();
                const grade = document.getElementById('grade').value.trim();
                const fromDate = document.getElementById('from_date').value;
                const toDate = document.getElementById('to_date').value;

                if (!academicDegree || !fromDate) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.fillAllRequired')}`);
                    return false;
                }

                if (toDate && new Date(toDate) < new Date(fromDate)) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.endDateAfterStart')}`);
                    return false;
                }

                return {
                    academic_degree: academicDegree,
                    education_authority: educationAuthority || null,
                    specialization: specialization || null,
                    grade: grade || null,
                    from_date: fromDate,
                    to_date: toDate || null
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.addEducationHistory(formValues);
                toast.success(t('employee.profile.educationAdded'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.educationAddFailed');
                toast.error(message);
            }
        }
    };

    const handleEditEducation = async (id) => {
        const currentEducationHistory = Array.isArray(educationHistory) ? educationHistory : [];
        const education = currentEducationHistory.find(e => e.id === id);
        if (!education) {
            toast.error(t('employee.profile.educationNotFound'));
            return;
        }

        const fromDate = education.from_date ? education.from_date.split('T')[0] : '';
        const toDate = education.to_date ? education.to_date.split('T')[0] : '';
        const safeAcademicDegree = (education.academic_degree || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const safeEducationAuthority = (education.education_authority || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const safeSpecialization = (education.specialization || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const safeGrade = (education.grade || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'right' : 'left';

        const { value: formValues } = await Swal.fire({
            title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">✏️ ${t('employee.profile.editEducation')}</div>`,
            html: `
                <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                            </svg>
                            ${t('employee.profile.academicDegree')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="academic_degree" class="swal2-input-modern" placeholder="${t('employee.profile.academicDegreePlaceholder')}" value="${safeAcademicDegree}" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            ${t('employee.profile.educationAuthority')}
                        </label>
                        <input id="education_authority" class="swal2-input-modern" placeholder="${t('employee.profile.educationAuthorityPlaceholder')}" value="${safeEducationAuthority}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                            </svg>
                            ${t('employee.profile.specialization')}
                        </label>
                        <input id="specialization" class="swal2-input-modern" placeholder="${t('employee.profile.specializationPlaceholder')}" value="${safeSpecialization}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                            </svg>
                            ${t('employee.profile.grade')}
                        </label>
                        <input id="grade" class="swal2-input-modern" placeholder="${t('employee.profile.gradePlaceholder')}" value="${safeGrade}" style="direction: ${dir}; text-align: ${textAlign};">
                    </div>


                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            ${t('employee.profile.fromDate')} <span style="color: #608fd3;">*</span>
                        </label>
                        <input id="from_date" type="date" class="swal2-input-modern" value="${fromDate}" style="direction: ${dir}; text-align: ${textAlign};" required>
                    </div>

                    <div class="form-field-group">
                        <label class="form-label">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            ${t('employee.profile.toDate')}
                        </label>
                        <input id="to_date" type="date" class="swal2-input-modern" value="${toDate}" style="direction: ${dir}; text-align: ${textAlign};">
                        <small class="form-hint">${t('employee.profile.graduationDate')}</small>
                    </div>
                </div>
            `,
            width: '600px',
            padding: '2rem',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('employee.profile.saveChanges')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            confirmButtonColor: '#608fd3',
            cancelButtonColor: '#6b7280',
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-popup-modern',
                title: 'swal2-title-modern',
                htmlContainer: 'swal2-html-container-modern',
                confirmButton: 'swal2-confirm-modern',
                cancelButton: 'swal2-cancel-modern'
            },
            preConfirm: () => {
                const academicDegree = document.getElementById('academic_degree').value.trim();
                const educationAuthority = document.getElementById('education_authority').value.trim();
                const specialization = document.getElementById('specialization').value.trim();
                const grade = document.getElementById('grade').value.trim();
                const fromDate = document.getElementById('from_date').value;
                const toDate = document.getElementById('to_date').value;

                if (!academicDegree || !fromDate) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.fillAllRequired')}`);
                    return false;
                }

                if (toDate && new Date(toDate) < new Date(fromDate)) {
                    Swal.showValidationMessage(`⚠️ ${t('employee.profile.endDateAfterStart')}`);
                    return false;
                }

                return {
                    academic_degree: academicDegree,
                    education_authority: educationAuthority || null,
                    specialization: specialization || null,
                    grade: grade || null,
                    from_date: fromDate,
                    to_date: toDate || null
                };
            }
        });

        if (formValues) {
            try {
                await employeeService.updateEducationHistory(id, formValues);
                toast.success(t('employee.profile.educationUpdated'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.educationUpdateFailed');
                toast.error(message);
            }
        }
    };

    const handleDeleteWorkExperience = async (id) => {
        const currentJobHistory = Array.isArray(jobHistory) ? jobHistory : [];
        const job = currentJobHistory.find(j => j.id === id);
        if (!job) return;
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'center' : 'center';

        const result = await Swal.fire({
            title: t('employee.profile.deleteConfirmTitle'),
            html: `<div style="text-align: ${textAlign}; direction: ${dir};">
                <p>${t('employee.profile.deleteConfirmText')}</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> ${t('employee.profile.delete')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-rtl',
                title: 'swal2-title-rtl',
                htmlContainer: 'swal2-html-container-rtl',
                confirmButton: 'swal2-confirm-delete-rtl',
                cancelButton: 'swal2-cancel-rtl'
            }
        });

        if (result.isConfirmed) {
            try {
                await employeeService.deleteJobHistory(id);
                toast.success(t('employee.profile.workExperienceDeleted'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.workExperienceDeleteFailed');
                toast.error(message);
            }
        }
    };

    const handleDeleteEducation = async (id) => {
        const currentEducationHistory = Array.isArray(educationHistory) ? educationHistory : [];
        const education = currentEducationHistory.find(e => e.id === id);
        if (!education) return;
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const textAlign = locale === 'ar' ? 'center' : 'center';

        const result = await Swal.fire({
            title: t('employee.profile.deleteConfirmTitle'),
            html: `<div style="text-align: ${textAlign}; direction: ${dir};">
                <p>${t('employee.profile.deleteConfirmText')}</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> ${t('employee.profile.delete')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('employee.profile.cancel')}</span>`,
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-rtl',
                title: 'swal2-title-rtl',
                htmlContainer: 'swal2-html-container-rtl',
                confirmButton: 'swal2-confirm-delete-rtl',
                cancelButton: 'swal2-cancel-rtl'
            }
        });

        if (result.isConfirmed) {
            try {
                await employeeService.deleteEducationHistory(id);
                toast.success(t('employee.profile.educationDeleted'));
                loadProfileData();
            } catch (error) {
                const message = error.response?.data?.message || t('employee.profile.educationDeleteFailed');
                toast.error(message);
            }
        }
    };

    const handleDownloadCV = () => {
        if (profile?.cv_file) {
            const cvUrl = profile.cv_file.startsWith('http')
                ? profile.cv_file
                : `${STORAGE_BASE_URL}/${profile.cv_file}`;
            window.open(cvUrl, '_blank');
        }
    };

    const handleAddNewCV = async () => {
        // Create a hidden file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.style.display = 'none';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                toast.error(t('employee.profile.uploadFailed'));
                return;
            }

            // Validate file size (10MB = 10 * 1024 * 1024 bytes)
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                toast.error(t('employee.profile.uploadFailed'));
                return;
            }

            // Show loading
            const loadingToast = toast.loading(t('employee.profile.uploadingImage'));

            try {
                await employeeService.uploadCv(file);
                toast.dismiss(loadingToast);
                toast.success(t('employee.profile.uploadSuccess'));
                loadProfileData();
                // Refresh user data to update CV file in localStorage
                await refreshUserData();
            } catch (error) {
                toast.dismiss(loadingToast);
                const message = error.response?.data?.message || t('employee.profile.uploadFailed');
                toast.error(message);
            }
        };

        // Trigger file input
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    };



    // Handle both wrapped and unwrapped profile responses

    const employee = profile || {};
    const userImageRaw = employee.user?.image || user?.image;
    // Add cache-busting parameter to force image refresh when updated
    const userImage = userImageRaw ? (userImageRaw.startsWith('http')
        ? `${userImageRaw}?t=${imageTimestamp}`
        : `${STORAGE_BASE_URL}/${userImageRaw}?t=${imageTimestamp}`) : null;
    const userName = employee.user?.name || user?.name || 'اسم المستخدم';
    const jobTitle = employee.job_title?.name || employee.user?.job_title_name || 'المسمي الوظيفي';
    const bio = employee.bio || 'هناك حقيقة مثبتة منذ زمن طويل وهي أن المحتوى المقروء لصفحة ما سيلهي القارئ عن التركيز على الشكل الخارجي للنص أو شكل توضع الفقرات في الصفحة التي يقرأها.';
    const cvFile = employee.cv_file;
    const cvFileName = employee.cv_file_name || (cvFile ? `${userName} cv` : '');
    const cvFileSize = employee.cv_file_size;
    const cvUploadedAt = employee.cv_upload_date || employee.cv_uploaded_at;

    // Ensure arrays are initialized
    const safeJobHistory = Array.isArray(jobHistory) ? jobHistory : [];
    const safeEducationHistory = Array.isArray(educationHistory) ? educationHistory : [];

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    return (
        <div className="employee-profile-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Notification and Settings */}
            {/* <div className="profile-header d-flex justify-content-between align-items-center px-3 py-3">
                <div className="d-flex gap-3">
                    <div className="notification-icon">
                        <span className="bell-icon">🔔</span>
                    </div>
                    <button className="settings-icon-btn" onClick={() => navigate('/employee/settings')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.0001 10.0001C14.8134 10.0001 13.6533 10.352 12.6666 11.0112C11.6799 11.6705 10.9109 12.6076 10.4568 13.704C10.0027 14.8003 9.88383 16.0067 10.1153 17.1706C10.3469 18.3345 10.9183 19.4036 11.7574 20.2427C12.5965 21.0818 13.6656 21.6533 14.8295 21.8848C15.9934 22.1163 17.1998 21.9975 18.2962 21.5433C19.3925 21.0892 20.3296 20.3202 20.9889 19.3335C21.6482 18.3468 22.0001 17.1867 22.0001 16.0001C21.9984 14.4093 21.3657 12.8841 20.2409 11.7592C19.116 10.6344 17.5908 10.0017 16.0001 10.0001ZM16.0001 20.0001C15.2089 20.0001 14.4356 19.7655 13.7778 19.3259C13.12 18.8864 12.6073 18.2617 12.3045 17.5308C12.0018 16.7999 11.9226 15.9956 12.0769 15.2197C12.2313 14.4438 12.6122 13.731 13.1716 13.1716C13.731 12.6122 14.4438 12.2313 15.2197 12.0769C15.9956 11.9226 16.7999 12.0018 17.5308 12.3045C18.2617 12.6073 18.8864 13.12 19.3259 13.7778C19.7655 14.4356 20.0001 15.2089 20.0001 16.0001C20.0001 17.0609 19.5786 18.0783 18.8285 18.8285C18.0783 19.5786 17.0609 20.0001 16.0001 20.0001ZM27.0001 16.2701C27.0051 16.0901 27.0051 15.9101 27.0001 15.7301L28.8651 13.4001C28.9628 13.2777 29.0305 13.1341 29.0627 12.9808C29.0948 12.8276 29.0905 12.6689 29.0501 12.5176C28.7443 11.3683 28.287 10.2649 27.6901 9.23631C27.6119 9.1017 27.5034 8.9872 27.3732 8.90192C27.243 8.81664 27.0947 8.76292 26.9401 8.74506L23.9751 8.41506C23.8517 8.28506 23.7267 8.16006 23.6001 8.04006L23.2501 5.06756C23.2321 4.91282 23.1782 4.76443 23.0926 4.63422C23.0071 4.50401 22.8924 4.39557 22.7576 4.31756C21.7286 3.72174 20.6253 3.26485 19.4763 2.95881C19.3249 2.91855 19.1662 2.91444 19.0129 2.94679C18.8596 2.97914 18.716 3.04705 18.5938 3.14506L16.2701 5.00006C16.0901 5.00006 15.9101 5.00006 15.7301 5.00006L13.4001 3.13881C13.2777 3.04102 13.1341 2.97333 12.9808 2.9412C12.8276 2.90906 12.6689 2.91338 12.5176 2.95381C11.3685 3.26009 10.2651 3.71741 9.23631 4.31381C9.1017 4.39196 8.9872 4.50046 8.90192 4.63067C8.81664 4.76087 8.76292 4.90919 8.74506 5.06381L8.41506 8.03381C8.28506 8.15797 8.16006 8.28297 8.04006 8.40881L5.06756 8.75006C4.91282 8.76806 4.76443 8.82196 4.63422 8.90747C4.50401 8.99298 4.39557 9.10772 4.31756 9.24256C3.72174 10.2715 3.26485 11.3749 2.95881 12.5238C2.91855 12.6752 2.91444 12.834 2.94679 12.9872C2.97914 13.1405 3.04705 13.2841 3.14506 13.4063L5.00006 15.7301C5.00006 15.9101 5.00006 16.0901 5.00006 16.2701L3.13881 18.6001C3.04102 18.7224 2.97333 18.866 2.9412 19.0193C2.90906 19.1726 2.91338 19.3312 2.95381 19.4826C3.25954 20.6318 3.71689 21.7352 4.31381 22.7638C4.39196 22.8984 4.50046 23.0129 4.63067 23.0982C4.76087 23.1835 4.90919 23.2372 5.06381 23.2551L8.02881 23.5851C8.15297 23.7151 8.27797 23.8401 8.40381 23.9601L8.75006 26.9326C8.76806 27.0873 8.82196 27.2357 8.90747 27.3659C8.99298 27.4961 9.10772 27.6045 9.24256 27.6826C10.2715 28.2784 11.3749 28.7353 12.5238 29.0413C12.6752 29.0816 12.834 29.0857 12.9872 29.0533C13.1405 29.021 13.2841 28.9531 13.4063 28.8551L15.7301 27.0001C15.9101 27.0051 16.0901 27.0051 16.2701 27.0001L18.6001 28.8651C18.7224 28.9628 18.866 29.0305 19.0193 29.0627C19.1726 29.0948 19.3312 29.0905 19.4826 29.0501C20.6318 28.7443 21.7352 28.287 22.7638 27.6901C22.8984 27.6119 23.0129 27.5034 23.0982 27.3732C23.1835 27.243 23.2372 27.0947 23.2551 26.9401L23.5851 23.9751C23.7151 23.8517 23.8401 23.7267 23.9601 23.6001L26.9326 23.2501C27.0873 23.2321 27.2357 23.1782 27.3659 23.0926C27.4961 23.0071 27.6045 22.8924 27.6826 22.7576C28.2784 21.7286 28.7353 20.6253 29.0413 19.4763C29.0816 19.3249 29.0857 19.1662 29.0533 19.0129C29.021 18.8596 28.9531 18.716 28.8551 18.5938L27.0001 16.2701ZM24.9876 15.4576C25.0088 15.8189 25.0088 16.1812 24.9876 16.5426C24.9727 16.79 25.0502 17.0341 25.2051 17.2276L26.9788 19.4438C26.7753 20.0906 26.5147 20.7181 26.2001 21.3188L23.3751 21.6388C23.129 21.6661 22.9019 21.7837 22.7376 21.9688C22.4969 22.2394 22.2407 22.4957 21.9701 22.7363C21.7849 22.9006 21.6674 23.1278 21.6401 23.3738L21.3263 26.1963C20.7257 26.5111 20.0982 26.7717 19.4513 26.9751L17.2338 25.2013C17.0564 25.0595 16.8359 24.9824 16.6088 24.9826H16.5488C16.1875 25.0038 15.8252 25.0038 15.4638 24.9826C15.2164 24.9677 14.9723 25.0452 14.7788 25.2001L12.5563 26.9751C11.9095 26.7715 11.282 26.5109 10.6813 26.1963L10.3613 23.3751C10.334 23.129 10.2164 22.9019 10.0313 22.7376C9.76069 22.4969 9.50442 22.2407 9.26381 21.9701C9.09947 21.7849 8.87233 21.6674 8.62631 21.6401L5.80381 21.3251C5.48905 20.7244 5.22843 20.0969 5.02506 19.4501L6.79881 17.2326C6.95369 17.0391 7.0312 16.795 7.01631 16.5476C6.99506 16.1862 6.99506 15.8239 7.01631 15.4626C7.0312 15.2152 6.95369 14.971 6.79881 14.7776L5.02506 12.5563C5.22859 11.9095 5.4892 11.282 5.80381 10.6813L8.62506 10.3613C8.87108 10.334 9.09822 10.2164 9.26256 10.0313C9.50317 9.76069 9.75944 9.50442 10.0301 9.26381C10.2159 9.09937 10.334 8.8717 10.3613 8.62506L10.6751 5.80381C11.2757 5.48905 11.9032 5.22843 12.5501 5.02506L14.7676 6.79881C14.961 6.95369 15.2052 7.0312 15.4526 7.01631C15.8139 6.99506 16.1762 6.99506 16.5376 7.01631C16.785 7.0312 17.0291 6.95369 17.2226 6.79881L19.4438 5.02506C20.0906 5.22859 20.7181 5.4892 21.3188 5.80381L21.6388 8.62506C21.6661 8.87108 21.7837 9.09822 21.9688 9.26256C22.2394 9.50317 22.4957 9.75944 22.7363 10.0301C22.9006 10.2152 23.1278 10.3327 23.3738 10.3601L26.1963 10.6738C26.5111 11.2744 26.7717 11.9019 26.9751 12.5488L25.2013 14.7663C25.0449 14.9614 24.9673 15.2081 24.9838 15.4576H24.9876Z" fill="#343330" />
                        </svg>
                    </button>
                </div>
                <div className="user-profile-header d-flex align-items-center gap-2">
                    <div className="profile-picture-small">
                        {userImage ? (
                            <img
                                src={userImage}
                                alt="Profile"
                                className="profile-img-small"
                                key={userImage} // Force re-render when image changes
                            />
                        ) : (
                            <div className="profile-img-small text-center align-content-center">
                                <b>{userName.charAt(0).toUpperCase()}</b>
                            </div>
                        )}
                    </div>
                    <div className="user-info text-end">
                        <div className="user-name">{userName}</div>
                        <div className="user-job-title">{jobTitle}</div>
                    </div>
                </div>
            </div> */}
            <EmployeeNav user={user} />

            {/* Main Profile Picture */}
            <div className="profile-picture-container d-flex justify-content-center flex-wrap mb-4">
                <div className="profile-picture-large position-relative">
                    {userImage ? (
                        <div>
                            <img
                                src={userImage}
                                alt="Profile"
                                className="profile-img-large"
                                key={userImage} // Force re-render when image changes
                            />

                        </div>
                    ) : (
                        <div className="profile-img-large text-center align-content-center">
                            <b style={{ fontSize: '4rem' }}>{userName.charAt(0).toUpperCase()}</b>
                        </div>
                    )}
                </div>
                <p className='d-block w-100 text-center'>{userName}</p>
            </div>

            {/* Job Title Section */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-2">
                    <h3 className="section-title">{t('employee.profile.jobTitle')}</h3>
                    <button className="edit-icon-btn" onClick={handleEditJobTitle}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </button>
                </div>
                <p className="section-text">{jobTitle !== t('employee.profile.jobTitle') ? jobTitle : t('employee.profile.jobTitleNotSet')}</p>
            </div>

            {/* About Me Section */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-2">
                    <h3 className="section-title">{t('employee.profile.aboutMe')}</h3>
                    <button className="edit-icon-btn" onClick={handleEditAbout}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </button>
                </div>
                <p className="section-text">{bio}</p>
            </div>

            {/* Work Experience Section */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                        <h3 className="section-title">{t('employee.profile.workExperience')}</h3>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="add-icon-btn" onClick={handleAddWorkExperience}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    </div>
                </div>
                {safeJobHistory.length > 0 ? (
                    <div className="experience-list mt-3">
                        {safeJobHistory.map((job, index) => (
                            <div key={job.id} className="experience-item">
                                <div className="d-flex justify-content-between">
                                    <div className="experience-title">{job.job_title?.name}</div>
                                    <div className="d-flex gap-2">
                                        {safeJobHistory.length > 0 && (
                                            <>
                                                <button className="edit-icon-btn" onClick={() => handleEditWorkExperience(job.id)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                <button className="delete-icon-btn" onClick={() => handleDeleteWorkExperience(job.id)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <ul className="experience-details">
                                    <li>{job.company_name}</li>
                                    <li>
                                        {t('employee.profile.from')} {formatDate(job.from_date)} {t('employee.profile.until')} {job.to_date ? formatDate(job.to_date) : t('employee.profile.untilNow')}
                                    </li>
                                </ul>
                                {/* hide in the last item */}
                                {index < safeJobHistory.length - 1 && (
                                    <hr className="my-2 " />
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="empty-state-text">{t('employee.profile.noWorkExperience')}</p>
                )}
            </div>

            {/* Education Section */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                            <path d="M6 12v5c3 3 9 3 12 0v-5" />
                        </svg>
                        <h3 className="section-title">{t('employee.profile.educationHistory')}</h3>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="add-icon-btn" onClick={handleAddEducation}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    </div>
                </div>
                {safeEducationHistory.length > 0 ? (
                    <div className="education-list mt-3">
                        {safeEducationHistory.map((edu, index) => (
                            <div key={edu.id} className="education-item">
                                <div className="d-flex justify-content-between">
                                    <div className="education-title">{edu.education_authority || 'اسم الجامعة'}</div>
                                    <div className="d-flex gap-2">
                                        {safeEducationHistory.length > 0 && (
                                            <>
                                                <button className="edit-icon-btn" onClick={() => handleEditEducation(edu.id)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                <button className="delete-icon-btn" onClick={() => handleDeleteEducation(edu.id)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <ul className="education-details">
                                    <li>{edu.academic_degree} {edu.specialization ? `- ${edu.specialization}` : ''}</li>
                                    <li>
                                        {t('employee.profile.from')} {formatDate(edu.from_date)} {t('employee.profile.until')} {edu.to_date ? formatDate(edu.to_date) : t('employee.profile.untilNow')}
                                    </li>
                                </ul>
                                {/* hide in the last item */}
                                {index < safeEducationHistory.length - 1 && (
                                    <hr className="my-2 " />
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="empty-state-text">{t('employee.profile.noEducationRecorded')}</p>
                )}
            </div>

            {/* Resume/CV Section */}
            <div className="profile-section px-3 mb-5">
                <h3 className="section-title mb-3">{t('employee.profile.cv')}</h3>

                {cvFile ? (
                    <div className="cv-card" >
                        <div className="d-flex justify-content-between align-items-center" onClick={handleDownloadCV}>
                            <div className="cv-info flex-grow-1">
                                <div className="cv-name">{cvFileName}</div>
                                <div className="cv-meta">
                                    {cvFileSize && formatFileSize(cvFileSize)}
                                    {cvUploadedAt && ` . ${formatDate(cvUploadedAt)}`}
                                    {cvUploadedAt && ` at ${new Date(cvUploadedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`}
                                </div>
                            </div>
                            <div className="cv-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#DC2626">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                            </div>
                        </div>
                        {/* add new cv button */}
                        <button className="btn-upload-cv-replace mt-3" onClick={handleAddNewCV}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{t('employee.profile.replaceCv')}</span>
                        </button>
                    </div>
                ) : (
                    <div className="cv-card-empty">
                        <p className="empty-state-text mb-3">{t('employee.profile.noCvText')}</p>
                        <button className="btn-upload-cv" onClick={handleAddNewCV}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{t('employee.profile.uploadCvButton')}</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default Profile;
